import { createContext, type PropsWithChildren, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import axios, { type AxiosError, isAxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { useAdminSettings } from './api';
import { type ClassSessionInfo, type ClassStudent, Roles } from './types';
import { filterSessionsByGroupAccess } from '../components/classroom/util';
import Loadable from '../components/common/loadable';
import {
    GET_CLASS_DATA_PATH,
    GET_STUDENT_FOR_CODE,
    OAUTH_CORP_LMS,
    STUDENT,
    GET_MERGED_CLASS_SETTINGS
} from '../config/apiPaths';
import { useUserUuid } from '../hooks/useUserUuid';
import { type OverridableClassOptions } from '../types/adminConfig';
import { type ClassData, type SchoolSettings } from '../types/apiTypes';
import { getUserUuidHeaders } from '../utils/httpUtils';
import { getUrlParams, processApiPath } from '../utils/url';

interface ClassContextProps {
    classUuid: string;
    classSessionUuid?: string;
}

interface ClassInfoContextInterface {
    isLoadingClassData: boolean;
    classData: ClassData; // raw data from GET_CLASS_DATA_PATH API call
    fetchClassInfo: () => void;
    errorMessage: string;
    classUuid: string;
    userUuid: string;
    me: Partial<ClassStudent>;
    meFetched: boolean;
    sessions: ClassSessionInfo[];
    adminSettings: SchoolSettings | null;
    previousSessions: ClassSessionInfo[] | undefined;
    upcomingSessions: ClassSessionInfo[] | undefined;
    setSessions: (sessions: ClassSessionInfo[]) => void;
    classSettings: OverridableClassOptions;
}

const sessionDateComparator = (a, b) => {
    const aDate = new Date(Number(a.start_time));
    const bDate = new Date(Number(b.start_time));
    if (aDate < bDate) return -1;
    if (aDate > bDate) return 1;
    return 0;
};

const ClassContext = createContext({} as ClassInfoContextInterface);

const { Provider, Consumer } = ClassContext;

const ClassProvider = (props: PropsWithChildren<ClassContextProps>) => {
    const { classUuid, classSessionUuid, children } = props;

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const [sessions, setSessions] = useState<ClassSessionInfo[]>([]);
    const [previousSessionsRaw, setPreviousSessionsRaw] = useState<ClassSessionInfo[] | undefined>(undefined);
    const [upcomingSessionsRaw, setUpcomingSessionsRaw] = useState<ClassSessionInfo[] | undefined>(undefined);
    const [previousSessions, setPreviousSessions] = useState<ClassSessionInfo[] | undefined>(undefined);
    const [upcomingSessions, setUpcomingSessions] = useState<ClassSessionInfo[] | undefined>(undefined);

    const { t } = useTranslation('error_messages');
    const userUuid = useUserUuid(null) || '';
    const { code, is_guest: isGuest, oauth_user, lti_creds } = getUrlParams();

    const { data: adminSettings } = useAdminSettings();

    const {
        data: me,
        isFetched: meFetched,
        isError: meError
    } = useQuery({
        queryKey: [STUDENT, userUuid],
        enabled: !!userUuid && !!classUuid,
        queryFn: async () => {
            if (me?.student_id) {
                return me;
            }
            if (!userUuid || !classUuid) {
                return null;
            }
            if (isGuest) {
                return null;
            }

            /**
             * Fetches user details based on user_uuid token.
             */
            if (!code || code === 'undefined') {
                try {
                    const response = await axios.get<Partial<ClassStudent>>(
                        processApiPath(STUDENT, undefined, {
                            class_uuid: classUuid
                        }),
                        { headers: getUserUuidHeaders(userUuid) }
                    );
                    return response.data;
                } catch (error) {
                    if (isAxiosError(error)) {
                        // STUDENT returns 404 if no user is found, if the oauth_user param is true then we can redirect to the oauth flow for the application (currrently only Docebo).
                        const axiosError = error as AxiosError;
                        if (axiosError.response?.status === 404) {
                            if (oauth_user) {
                                const redirectUrl = processApiPath(OAUTH_CORP_LMS, undefined, {
                                    class_uuid: classUuid,
                                    class_session_uuid: classSessionUuid || '',
                                    lti_creds_uuid: lti_creds || ''
                                });
                                window.location.href = redirectUrl;
                                // wait for 1 second and then return to avoid the error message from showing up in UI since we are just redirecting the user
                                const delay = new Promise((resolve) => setTimeout(resolve, 1000));
                                return await delay;
                            }
                        }
                    }
                    throw error;
                }
            }

            /**
             * Exchanges an LTI code for user authentication details.
             * The user_uuid is associated with a user after exch.
             */
            if (code && code !== 'undefined') {
                const response = await axios.get<{ student: Partial<ClassStudent> }>(
                    processApiPath(GET_STUDENT_FOR_CODE, undefined, {
                        verification_code: code,
                        class_uuid: classUuid
                    }),
                    { headers: getUserUuidHeaders(userUuid) }
                );

                return response.data.student;
            }

            return undefined;
        }
    });

    const {
        data: classData,
        refetch: fetchClassData,
        isLoading: isLoadingClassData,
        isError: classDataError
    } = useQuery({
        queryKey: [GET_CLASS_DATA_PATH, classUuid],
        enabled: !!userUuid && !!me?.student_id,
        refetchOnWindowFocus: false,
        queryFn: async () => {
            if (!userUuid || !me?.student_id) {
                return null;
            }

            const { data: classDataResponse } = await axios.post<ClassData>(
                processApiPath(GET_CLASS_DATA_PATH),
                {
                    class_uuid: classUuid,
                    with_info: 'true',
                    with_rms_data: 'true'
                },
                { headers: getUserUuidHeaders(userUuid) }
            );

            if (classDataResponse && classDataResponse.classSessions && classDataResponse.classSessionGroups) {
                const classSessionGroupsByUuid = {};

                classDataResponse.classSessionGroups.forEach((classSessionGroup) => {
                    classSessionGroupsByUuid[classSessionGroup.class_session_group_uuid] = classSessionGroup;
                });

                classDataResponse.classSessions.forEach((classSession) => {
                    if (classSession.class_session_group_uuid) {
                        classSession.class_session_group =
                            classSessionGroupsByUuid[classSession.class_session_group_uuid];
                    }
                });
            }
            return classDataResponse;
        }
    });

    const {
        data: classSettings,
        isFetched: classSettingsFetched,
        isError: classSettingsError
    } = useQuery({
        queryKey: [GET_MERGED_CLASS_SETTINGS, classUuid],
        enabled: !!classUuid,
        refetchOnWindowFocus: false,
        queryFn: async () => {
            if (!classUuid) {
                return null;
            }

            const { data } = await axios.get(processApiPath(GET_MERGED_CLASS_SETTINGS), {
                params: {
                    class_uuid: classUuid
                }
            });
            return data.options as OverridableClassOptions;
        }
    });

    useEffect(() => {
        if (!classData) {
            return;
        }

        const sortedSessions = classData.classSessions.sort(sessionDateComparator);

        const now = Date.now();
        const previous = sortedSessions.filter((s) => new Date(Number(s.end_time) * 1000).getTime() < now);
        const upcoming = sortedSessions.filter((s) => new Date(Number(s.end_time) * 1000).getTime() >= now);

        setSessions(sortedSessions);
        setPreviousSessionsRaw(previous);
        setUpcomingSessionsRaw(upcoming);
    }, [classData]);

    useEffect(() => {
        if (!meError || oauth_user) {
            return;
        }
        const key = enqueueSnackbar(t('class:error-message'), { variant: 'error' });

        return () => {
            closeSnackbar(key);
        };
    }, [meError, enqueueSnackbar, closeSnackbar, t, oauth_user]);

    useEffect(() => {
        if (!adminSettings || !previousSessionsRaw || !upcomingSessionsRaw || !me || !classData) {
            return;
        }

        const privilegedUser = me.role === Roles.Teacher || me.role === Roles.Assistant;
        const restrictSessions = adminSettings?.restrict_sessions_recordings_by_groups;

        if (restrictSessions && !privilegedUser && classData.classGroups.length) {
            // Filter sessions where the current user is a member of group(s) attributed to the session.
            const previous = filterSessionsByGroupAccess(
                previousSessionsRaw,
                classData.classGroups,
                classData.userGroups,
                me
            );

            const upcoming = filterSessionsByGroupAccess(
                upcomingSessionsRaw,
                classData.classGroups,
                classData.userGroups,
                me
            );

            setPreviousSessions(previous);
            setUpcomingSessions(upcoming);
        } else {
            setPreviousSessions(previousSessionsRaw);
            setUpcomingSessions(upcomingSessionsRaw);
        }
    }, [adminSettings, classData, me, previousSessionsRaw, upcomingSessionsRaw]);

    return useMemo(() => {
        if (!classData || !adminSettings || !me || !classSettings) {
            return <Loadable isLoading fullScreen />;
        }

        const value: ClassInfoContextInterface = {
            isLoadingClassData,
            adminSettings,
            classData,
            fetchClassInfo: fetchClassData,
            errorMessage: classDataError ? t('class:error-message') : '',
            classUuid,
            userUuid,
            me,
            meFetched,
            sessions,
            setSessions,
            previousSessions,
            upcomingSessions,
            classSettings
        };

        return <Provider value={value}>{children}</Provider>;
    }, [
        adminSettings,
        children,
        classData,
        classDataError,
        classSettings,
        classUuid,
        fetchClassData,
        isLoadingClassData,
        me,
        meFetched,
        previousSessions,
        sessions,
        t,
        upcomingSessions,
        userUuid
    ]);
};

export { ClassContext, ClassProvider, Consumer as SessionConsumer };
